import { useFormikContext } from "formik";
import React, { useCallback, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { CANADIAN_STATES, US_STATES } from "../../../../../constants";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import CheckGreenCircleIcon from "../../../../assets/svg/icons/common/check-green-circle";
import AddressAutocomplete from "../../../../components/form/address-autocomplete";
import Select from "../../../../components/form/select";
import SlidingToggle from "../../../../components/form/sliding-checkbox";
import TextInput from "../../../../components/form/text-input";
import useUserStore from "../../../../stores/user";
import { googlePlaceToFormData } from "../../../../utils/address-parser";
import baseStyles from "../../style.module.scss";
import { contactInfoSchema } from "../../validation-schema";
import styles from "./style.module.scss";

// take this out to a local constants.js. Will work similarly for Canadian provinces
const US_STATE_LIST = Object.entries(US_STATES).map(([key, value]) => {
  return {
    label: value,
    value: key,
  };
});

const CANADIAN_STATE_LIST = Object.entries(CANADIAN_STATES).map(
  ([key, value]) => {
    return {
      label: value,
      value: key,
    };
  },
);

const ContactInformation = ({ isOpen, onToggle }) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    setErrors,
  } = useFormikContext();
  const selectedCountry = values.country;
  const isCanada = selectedCountry === "ca";
  const { isEMSUser, userLoading } = useUserStore();
  const isSectionValid = contactInfoSchema.isValidSync(values);

  const handleDbaAddressSelected = useCallback(
    (place) => {
      const addressData = googlePlaceToFormData(place, {
        streetAddress: "dba_street",
        city: "dba_city",
        state: "dba_state",
        zipCode: isCanada ? "postal_code" : "dba_zip",
        country: "country",
      });

      if (addressData.dba_state) {
        const stateList = isCanada ? CANADIAN_STATE_LIST : US_STATE_LIST;

        const matchingState = stateList.find(
          (option) =>
            option.label === addressData.dba_state ||
            option.value === addressData.dba_state,
        );

        if (matchingState) {
          addressData.dba_state = matchingState.value;
        } else {
          console.log(
            "No matching state found in dropdown for:",
            addressData.dba_state,
          );
        }
      }

      // NOTE: This is a hack/weird way to set a country; which is doubled up
      // as a form language field on the backend.
      // Set the country to lowercase 'ca' if it's Canada
      // Set the country to lowercase 'en' if it's USA
      addressData.country =
        addressData?.country?.toLowerCase() === "ca" ? "ca" : "en";

      Object.entries(addressData).forEach(([field, value]) => {
        setFieldValue(field, value || "");
      });
    },
    [isCanada, setFieldValue],
  );

  return (
    <div className={styles.contact_information}>
      <div className={styles.header} onClick={onToggle}>
        <div
          className={`${styles.header_left} ${baseStyles.headerLeft} ${isOpen && baseStyles.headerLeftOpen}`}
        >
          <CaretDown24 />
          <h6 className={styles.title}>Contact Information</h6>
        </div>
        <div className={baseStyles.headerRight}>
          {isSectionValid && <CheckGreenCircleIcon />}
        </div>
      </div>
      {isOpen && (
        <div className={`${styles.content} ${styles.form}`}>
          <div className={styles.form_group}>
            <TextInput
              label={"Owner First Name"}
              placeholder={"First Name"}
              type="text"
              id="first_name"
              name="first_name"
              value={values.first_name}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.first_name}
              errored={errors.first_name}
              error={errors.first_name}
            />

            <TextInput
              label={"Owner Last Name"}
              placeholder={"Last Name"}
              type="text"
              id="last_name"
              name="last_name"
              value={values.last_name}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.last_name}
              errored={errors.last_name}
              error={errors.last_name}
            />
          </div>

          <div className={styles.form_group}>
            <TextInput
              label={"DBA Name"}
              placeholder={"DBA Name"}
              type="text"
              id="dba_name"
              name="dba_name"
              value={values.dba_name}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.dba_name}
              errored={errors.dba_name}
              error={errors.dba_name}
            />
          </div>

          <div className={styles.form_group}>
            <AddressAutocomplete
              label={"DBA Address"}
              placeholder={"DBA Address"}
              type="text"
              id="dba_street"
              name="dba_street"
              value={values.dba_street}
              onChange={handleChange}
              onBlur={handleBlur}
              onPlaceSelected={handleDbaAddressSelected}
              touched={touched.dba_street}
              errored={errors.dba_street}
              error={errors.dba_street}
              countryRestriction={isCanada ? "ca" : "us"}
            />

            <TextInput
              label={"DBA City"}
              placeholder={"City"}
              type="text"
              id="dba_city"
              name="dba_city"
              value={values.dba_city}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.dba_city}
              errored={errors.dba_city}
              error={errors.dba_city}
            />
          </div>

          <div className={styles.form_group}>
            <Select
              label={isCanada ? "DBA Province" : "DBA State"}
              placeholder={isCanada ? "Select Province" : "Select State"}
              id="dba_state"
              name="dba_state"
              options={isCanada ? CANADIAN_STATE_LIST : US_STATE_LIST}
              search
              value={values?.dba_state}
              onChange={(value) => {
                handleChange({ target: { name: "dba_state", value } });
              }}
              onBlur={handleBlur}
              error={
                errors?.dba_state && touched?.dba_state
                  ? errors.dba_state
                  : false
              }
            />
            <TextInput
              label={isCanada ? "DBA Postal Code" : "DBA Zip"}
              placeholder={isCanada ? "Postal Code" : "Zip"}
              type="text"
              id={isCanada ? "postal_code" : "dba_zip"}
              name={isCanada ? "postal_code" : "dba_zip"}
              value={isCanada ? values.postal_code : values.dba_zip}
              touched={isCanada ? touched.postal_code : touched.dba_zip}
              errored={isCanada ? errors.postal_code : errors.dba_zip}
              error={isCanada ? errors.postal_code : errors.dba_zip}
              onBlur={handleBlur}
              onChange={() => {}}
              cleaveOptions={
                isCanada
                  ? {
                      delimiters: [" "],
                      blocks: [3, 3],
                      uppercase: true,
                      alphanumericOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "postal_code",
                            value: e.target.value,
                          },
                        });
                      },
                    }
                  : {
                      blocks: [5],
                      numericOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "dba_zip",
                            value: e.target.value,
                          },
                        });
                      },
                    }
              }
            />
          </div>

          <div className={styles.form_group}>
            <TextInput
              label={"Business Email"}
              placeholder={"Business Email"}
              type="text"
              id="email"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              touched={touched.email}
              errored={errors.email}
              error={errors.email}
            />
            <TextInput
              label={"Business Phone"}
              placeholder={"Business Phone"}
              type="text"
              id="mobile"
              name="mobile"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.mobile}
              touched={touched.mobile}
              errored={errors.mobile}
              error={errors.mobile}
              cleaveOptions={{
                blocks: [3, 3, 4],
                delimiter: "-",
                numericOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: { name: "mobile", value: e.target.value },
                  });
                },
              }}
            />
          </div>

          {!userLoading && isEMSUser && (
            <div className={styles.form_group}>
              <div className={styles.merchant_referral_section}>
                <div className={styles.merchant_referral_toggles}>
                  <div className={styles.toggle_group}>
                    <p className={styles.p_heading}>Merchant Referral</p>
                    <SlidingToggle
                      width="150px"
                      label="Merchant Referral"
                      id="is_merchant_referral"
                      name="is_merchant_referral"
                      onChange={(value) => {
                        setFieldValue("is_merchant_referral", value);
                        if (value === "no") {
                          setFieldValue("referrer_name", "");
                          setFieldValue("referrer_mid", "");
                          setErrors({
                            referrer_name: "",
                            referrer_mid: "",
                          });
                        }
                      }}
                      value={values.is_merchant_referral}
                      options={["yes", "no"]}
                    />
                  </div>

                  <div className={styles.toggle_group}>
                    <p className={styles.p_heading}>Cold Call </p>
                    <SlidingToggle
                      width="150px"
                      label="Cold Call"
                      id="cold_call"
                      name="cold_call"
                      onChange={(value) => {
                        setFieldValue("cold_call", value);
                      }}
                      value={values.cold_call}
                      options={["yes", "no"]}
                    />
                  </div>

                  <div className={styles.toggle_group}>
                    <p className={styles.p_heading}>TM Lead</p>
                    <SlidingToggle
                      width="150px"
                      label="TM Lead"
                      id="tm_lead"
                      name="tm_lead"
                      onChange={(value) => {
                        setFieldValue("tm_lead", value);
                      }}
                      value={values.tm_lead}
                      options={["yes", "no"]}
                    />
                  </div>

                  <div className={styles.toggle_group}>
                    <p className={styles.p_heading}>TM Name</p>
                    <SlidingToggle
                      width="150px"
                      label="TM Name"
                      id="is_tm"
                      name="is_tm"
                      onChange={(value) => {
                        setFieldValue("is_tm", value);
                        if (value === "no") {
                          setFieldValue("telemarketer_name", "");
                        }
                      }}
                      value={values.is_tm}
                      options={["yes", "no"]}
                    />
                  </div>
                </div>

                {values.is_merchant_referral === "yes" && (
                  <>
                    <div className={styles.form_group}>
                      <TextInput
                        label={"Referrer Business Name"}
                        placeholder={"Referrer Business Name"}
                        type="text"
                        id="referrer_name"
                        name="referrer_name"
                        value={values.referrer_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.referrer_name}
                        errored={errors.referrer_name}
                        error={errors.referrer_name}
                        readOnly={values.is_merchant_referral === "no"}
                      />
                    </div>
                    <div className={styles.form_group}>
                      <TextInput
                        label={"Referrer MID"}
                        placeholder={"Referrer MID"}
                        type="text"
                        id="referrer_mid"
                        name="referrer_mid"
                        value={values.referrer_mid}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.referrer_mid}
                        errored={errors.referrer_mid}
                        error={errors.referrer_mid}
                        readOnly={values.is_merchant_referral === "no"}
                      />
                    </div>
                  </>
                )}

                {values.is_tm === "yes" && (
                  <div className={styles.form_group}>
                    <TextInput
                      label={"Telemarketer's Name"}
                      placeholder={"Telemarketer's Name"}
                      type="text"
                      id="telemarketer_name"
                      name="telemarketer_name"
                      value={values.telemarketer_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.telemarketer_name}
                      errored={errors.telemarketer_name}
                      error={errors.telemarketer_name}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactInformation;
